import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ButtonSection,
  Content,
  Form,
  FormBlock,
  Heading,
  Input,
  InputDiv,
  ItemDiv,
  Label,
  LeftSection,
  SelectedText,
  SelectInput,
  SubHeading,
  TitleSection,
  TopSection,
  ViewSection,
  ViewTab,
  ViewTabItem,
  ViewTabItemLabel,
  ViewTabItemIcon,
  OuterDiv,
  IconDiv
} from './style';
import ButtonV2 from '../../../../../component/button/buttonV2';
import AddIconDropdown from '../../../../../component/viewCard/addIconDropdown';
import { rootStore } from '../../../../../mobx/rootStore';
import { isEmpty } from 'lodash';
import { Dropdown, DropdownItem } from '../../../../../component/Dropdown';
import SVGIcon from '../../../../../assets/images/svg/SVGIcon';
import { useSelector } from 'react-redux';
import { RootReducerInterface } from '../../../../../interfaces/RootReducerInterface';
import { CustomerDetailsInterface } from '../../../../../interfaces/CustomerInterface';
import DatePicker from '../../../../../component/dateTime/datePicker';
import { CreateProjectStep2ViewItems } from '../../../../../global/row';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, TASK_LIST_TYPE } from '../../../../../global/constants';
import { dispatchProjectError } from '../../../../../services/projectServices';
import { useMobileDevice } from '../../../../../global/useMobile';
import { ProjectTemplateModel } from '../../../../../mobx/models/projectTemplate';
interface Props {
  onClickCreate: () => void;
}

const ProjectDetails: React.FC<Props> = (props: Props) => {
  const { onClickCreate } = props;
  // use state variables
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetailsInterface | null>(null);
  const [templateDetails, setTemplateDetails] = useState<ProjectTemplateModel | null>(null);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchTemplate, setSearchTemplate] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [projectView, setProjectView] = useState<Number>(TASK_LIST_TYPE.TASKS);
  // select state variables
  const stateSelector = useSelector((state: RootReducerInterface) => state);
  const { customer: customerSelector } = stateSelector || {};
  const { customers } = customerSelector;
  // mobx variables
  const { projectStore, projectTemplateStore } = rootStore;
  const { createProjectInput } = projectStore;
  const { customerId, startDate, dueDate } = createProjectInput;
  const { projectTemplates } = projectTemplateStore;
  // other variables
  const isMobile = useMobileDevice();

  // update customer
  const updateCustomer = useCallback(
    (customer: CustomerDetailsInterface) => {
      createProjectInput.setCustomerId(customer?.id);
      setCustomerDetails(customer);
    },
    [createProjectInput]
  );

  const updateTemplate = useCallback(
    (template: ProjectTemplateModel) => {
      if (!template?.id) return;
      createProjectInput.setTemplateId(template?.id);
      setTemplateDetails(template);
    },
    [createProjectInput]
  );

  useEffect(() => {
    if (!isEmpty(createProjectInput?.customerId)) {
      const selectedCustomer = customers?.find((item) => item?.id === createProjectInput?.customerId);
      if (selectedCustomer) updateCustomer(selectedCustomer);
    }
    if (isEmpty(createProjectInput?.startDate)) createProjectInput?.setStartDate(new Date());
  }, [createProjectInput, createProjectInput?.customerId, customers, projectTemplates, updateCustomer, updateTemplate]);

  // onChange search
  const onChangeSearch = useCallback((value: string, isTemplate?: boolean) => {
    setIsSearch(true);
    if (isTemplate) {
      setSearchTemplate(value);
    } else {
      setSearchCustomer(value);
    }
  }, []);

  const handleCreate = useCallback(() => {
    if (isEmpty(createProjectInput?.name)) {
      dispatchProjectError('Please add project name');
      return;
    }
    onClickCreate();
  }, [createProjectInput, onClickCreate]);

  const handleToggleProjectDropdown = useCallback(() => {
    createProjectInput?.setCustomerId(customerDetails?.id || '');
    setIsCustomerDropdownOpen((prev) => !prev);
  }, [createProjectInput, customerDetails?.id]);

  const handleToggleTemplateDropdown = useCallback(() => {
    createProjectInput?.setTemplateId(templateDetails?.id || '');
    setIsTemplateDropdownOpen((prev) => !prev);
  }, [createProjectInput, templateDetails?.id]);

  const onChangeStartDate = useCallback(
    (date: any) => {
      if (dueDate && dueDate < date) {
        createProjectInput.setDueDate(null);
      }
      if (date === null) {
        rootStore.projectStore.createProjectInput.setStartDate(null);
      } else {
        rootStore.projectStore.createProjectInput.setStartDate(new Date(date));
      }
    },
    [createProjectInput, dueDate]
  );

  const onChangeEndDate = useCallback((date: any) => {
    if (date === null) {
      rootStore.projectStore.createProjectInput.setDueDate(null);
    } else {
      rootStore.projectStore.createProjectInput.setDueDate(date);
    }
  }, []);

  // render customer dropdown
  const renderCustomerDropdown = useMemo(() => {
    const filteredCustomer = !isSearch
      ? customers
      : customers?.filter((x) => x?.value?.trim().toLowerCase().includes(searchCustomer.toLowerCase()));
    return filteredCustomer?.map((item) => {
      const isSelected = item.id === customerId;
      return (
        <>
          <DropdownItem
            key={item.id}
            label={item.value}
            onClick={() => {
              setIsSearch(false);
              setSearchCustomer('');
              updateCustomer(item);
              setIsCustomerDropdownOpen(false);
            }}
            isSuffix={
              isSelected && <SVGIcon name='checkbox-icon' width='16' height='16' viewBox='0 0 16 16' fill='none' />
            }
          />
        </>
      );
    });
  }, [customerId, customers, isSearch, searchCustomer, updateCustomer]);

  const renderTemplateDropdown = useMemo(() => {
    const filteredTemplate = !isSearch
      ? projectTemplates
      : projectTemplates?.filter((x) => x?.value?.trim().toLowerCase().includes(searchTemplate.toLowerCase()));
    return filteredTemplate?.map((item) => {
      const isSelected = item.id === customerId;
      return (
        <>
          <DropdownItem
            key={item.id}
            label={item.name}
            onClick={() => {
              setIsSearch(false);
              setSearchTemplate('');
              updateTemplate(item);
              setIsTemplateDropdownOpen(false);
            }}
            isSuffix={
              isSelected && <SVGIcon name='checkbox-icon' width='16' height='16' viewBox='0 0 16 16' fill='none' />
            }
          />
        </>
      );
    });
  }, [customerId, isSearch, projectTemplates, searchTemplate, updateTemplate]);

  // render view tabs
  const renderViewTabs = useMemo(() => {
    return CreateProjectStep2ViewItems.map((item) => {
      return (
        // need to make isActive working
        <ViewTabItem key={item?.key} isActive={projectView === item?.key} onClick={() => setProjectView(item?.key)}>
          <ViewTabItemIcon>
            <SVGIcon name={item?.iconName} width='16' height='16' viewBox='0 0 18 18' fill='var(--text-secondary)' />
          </ViewTabItemIcon>
          <ViewTabItemLabel>{item?.label}</ViewTabItemLabel>
        </ViewTabItem>
      );
    });
  }, [projectView]);

  return (
    <>
      <OuterDiv>
        <LeftSection>
          <TopSection>
            <TitleSection>
              {!isMobile && <Heading>Add project details</Heading>}
              <SubHeading>Fill in the information to create project.</SubHeading>
            </TitleSection>
            <Content>
              <AddIconDropdown iconSize={20} size={38} isCreateProject={true} />
              <Form>
                <Label>Project name</Label>
                <Input
                  placeholder='Enter project name'
                  value={createProjectInput.name}
                  onChange={(e) => {
                    createProjectInput.setName(e.target.value);
                  }}
                />
              </Form>
              {customers && customers?.length > 0 && (
                <Form>
                  <Label>Customer</Label>
                  <Dropdown
                    modalTitle='Customer'
                    isMinWidth={398}
                    onOutsideClick={() => setIsCustomerDropdownOpen(false)}
                    content={
                      <InputDiv onClick={handleToggleProjectDropdown}>
                        <SelectInput
                          placeholder={isEmpty(customerDetails?.companyName) ? 'Select a customer' : ''}
                          value={searchCustomer}
                          onChange={(e) => onChangeSearch(e.target.value)}
                        />
                        {isEmpty(searchCustomer) && <SelectedText>{customerDetails?.companyName}</SelectedText>}
                        <IconDiv>
                          <SVGIcon
                            name='new-angleDown-icon'
                            width='18'
                            height='18'
                            viewBox='0 0 18 18'
                            fill='var(--text-secondary)'
                            className={`angleDown-icon ${isCustomerDropdownOpen ? 'rotate' : ''}`}
                          />
                        </IconDiv>
                      </InputDiv>
                    }
                    trigger='click'>
                    <ItemDiv>{renderCustomerDropdown}</ItemDiv>
                  </Dropdown>
                </Form>
              )}
              {projectTemplates && projectTemplates?.length > 0 && (
                <Form>
                  <Label>Template</Label>
                  <Dropdown
                    modalTitle='Template'
                    isMinWidth={398}
                    onOutsideClick={() => setIsTemplateDropdownOpen(false)}
                    content={
                      <InputDiv onClick={handleToggleTemplateDropdown}>
                        <SelectInput
                          placeholder={isEmpty(templateDetails?.name) ? 'Select a template' : ''}
                          value={searchTemplate}
                          onChange={(e) => onChangeSearch(e.target.value, true)}
                        />
                        {isEmpty(searchTemplate) && <SelectedText>{templateDetails?.name}</SelectedText>}
                        <IconDiv>
                          <SVGIcon
                            name='new-angleDown-icon'
                            width='18'
                            height='18'
                            viewBox='0 0 18 18'
                            fill='var(--text-secondary)'
                            className={`angleDown-icon ${isTemplateDropdownOpen ? 'rotate' : ''}`}
                          />
                        </IconDiv>
                      </InputDiv>
                    }
                    trigger='click'>
                    <ItemDiv>{renderTemplateDropdown}</ItemDiv>
                  </Dropdown>
                </Form>
              )}
              <FormBlock>
                <Form>
                  <Label>Start date</Label>
                  <DatePicker
                    label='Select start date'
                    value={startDate}
                    className='dueDate'
                    onChange={onChangeStartDate}
                  />
                </Form>
                <Form>
                  <Label>End date (Optional)</Label>
                  <DatePicker
                    label='Select end date'
                    value={dueDate ? dueDate : undefined}
                    className='dueDate'
                    minDate={startDate ?? new Date()}
                    onChange={onChangeEndDate}
                  />
                </Form>
              </FormBlock>
              {false && (
                <ViewSection>
                  <Label>Views</Label>
                  <ViewTab>{renderViewTabs}</ViewTab>
                </ViewSection>
              )}
            </Content>
          </TopSection>
          <ButtonSection>
            <ButtonV2 title='Create Project' variant={BUTTON_TYPE.PRIMARY} onClick={handleCreate} isLoading={false} />
          </ButtonSection>
        </LeftSection>
      </OuterDiv>
    </>
  );
};

export default observer(ProjectDetails);
